import React from 'react';
import Container from '../layout/Container';
import Eyebrow from '../ui/Eyebrow';
import SectionHeading from '../ui/SectionHeading';
import CountUp from '../ui/CountUp';
import { colors, font } from '../../lib/tokens';

type Metric = {
  value: string;
  /** Count-up spec for figures that animate in; `value` is shown as-is otherwise. */
  count?: React.ComponentProps<typeof CountUp>;
  label: string;
  src?: string;
};

const METRICS: Metric[] = [
  { value: '10,000+', count: { to: 10000, suffix: '+' }, label: 'ICD-10 codes a clinician must navigate per encounter' },
  { value: '$31.7B',  count: { to: 31.7, decimals: 1, prefix: '$', suffix: 'B' }, label: 'Medicare improper payments, FY\u00A02024', src: '¹' },
  { value: '16 min',  count: { to: 16, suffix: ' min' }, label: 'Average EHR time per patient encounter', src: '²' },
  { value: '~1 in 5', label: 'Diagnosis codes in routine hospital data that don’t match the case notes', src: '³' },
];

type Citation = { marker: string; text: string; href: string };

const CITATIONS: Citation[] = [
  {
    marker: '¹',
    text: 'CMS Improper Payments Fact Sheet, FY 2024',
    href: 'https://www.cms.gov/newsroom/fact-sheets/fiscal-year-2024-improper-payments-fact-sheet',
  },
  {
    marker: '²',
    text: 'Overhage & McCallie, Ann Intern Med, 2020',
    href: 'https://www.acpjournals.org/doi/10.7326/M18-3684',
  },
  {
    marker: '³',
    text: 'Burns et al, J Public Health 2012, systematic review: median diagnostic accuracy 80.3%',
    href: 'https://pubmed.ncbi.nlm.nih.gov/21795302/',
  },
];

// "The problem" metrics grid — reuses the ProblemSolutionSection slot from the
// original layout so LandingPage wiring stays the same.
export default function ProblemSolutionSection() {
  return (
    <section
      id="problem"
      className="kc-section"
      style={{
        background: colors.surface,
        borderTop: `1px solid ${colors.hair}`,
      }}
    >
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
          <div data-reveal>
            <Eyebrow color={colors.ember}>The problem</Eyebrow>
            <SectionHeading>
              Coding is slow, costly,
              {' '}<br />
              and full of mistakes.
            </SectionHeading>
            <p
              style={{
                color: colors.muted,
                marginTop: 20,
                fontSize: 16,
                lineHeight: 1.65,
                maxWidth: 400,
              }}
            >
              Clinicians burn hours translating free-text into codes. Under-coding leaves revenue on
              the floor; over-coding attracts penalties. The system rewards no one.
            </p>
          </div>
          <div
            data-reveal
            className="kc-metrics-grid"
            style={{
              '--d': '80ms',
              background: colors.hair,
              border: `1px solid ${colors.hair}`,
            } as React.CSSProperties}
          >
            {METRICS.map((m) => (
              <div
                key={m.value}
                className="kc-metrics-cell"
                style={{ background: colors.surface }}
              >
                <div
                  className="kc-metrics-value"
                  style={{
                    fontFamily: font.sans,
                    fontWeight: 500,
                    color: colors.ink,
                    lineHeight: 1,
                  }}
                >
                  {m.count ? <CountUp {...m.count} /> : m.value}
                  {m.src && (
                    <sup
                      style={{
                        fontSize: 14,
                        color: colors.dim,
                        fontWeight: 400,
                        marginLeft: 2,
                      }}
                    >
                      {m.src}
                    </sup>
                  )}
                </div>
                <div style={{ color: colors.muted, fontSize: 14, marginTop: 12, lineHeight: 1.5 }}>
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          data-reveal
          style={{
            fontFamily: font.mono,
            fontSize: 11,
            color: colors.muted,
            letterSpacing: 0.2,
            marginTop: 28,
            lineHeight: 1.9,
            maxWidth: 980,
          }}
        >
          {CITATIONS.map((c, i) => (
            <React.Fragment key={c.marker}>
              {i > 0 && <span style={{ color: colors.dim }}>&nbsp;·&nbsp;</span>}
              <a href={c.href} target="_blank" rel="noreferrer noopener" className="kc-cite-link">
                {c.marker} {c.text}
              </a>
            </React.Fragment>
          ))}
        </div>
      </Container>
    </section>
  );
}
